import { useQuery } from "@tanstack/react-query";
import { AlertTriangle, X } from "lucide-react";
import React from "react";

import { type BucketCellRef, getShelfLifeBucketItems } from "@/api/warehouse_dashboard";
import { Button } from "@/components/ui/button";
import { fmtDate, fmtPercent, ruNum } from "@/lib/format";
import { useBackGuard } from "@/lib/scan-ui/back-guard";
import { LoadState } from "@/lib/scan-ui/screen";

/**
 * Позиции одной ячейки ОСГ (юрлицо × склад × корзина).
 *
 * Итог внизу равен числу в ячейке сводки: корзину бэкенд считает той же
 * функцией, фронт ничего не пересчитывает и не сортирует — порядок «самое
 * срочное сверху» приходит с сервера. Заголовок берётся из сводки, чтобы он
 * был виден и пока позиции грузятся, и при ошибке.
 *
 * Своя разметка, а не `lib/scan-ui/overlays.Dialog`: тот — подтверждение с
 * двумя кнопками, здесь же просмотр широкой таблицы. Аппаратная «назад»
 * закрывает модалку (`useBackGuard`), как у остальных диалогов портала.
 */
export function BucketItemsDialog({
  cell,
  title,
  onClose,
}: {
  cell: BucketCellRef;
  /** «юрлицо · склад · корзина» — из сводки. */
  title: string;
  onClose: () => void;
}): React.ReactElement {
  useBackGuard(true, onClose);
  const q = useQuery({
    queryKey: [
      "warehouse-dashboard-bucket-items",
      cell.organization_ref,
      cell.warehouse_ref,
      cell.bucket,
    ],
    queryFn: () => getShelfLifeBucketItems(cell),
    // Холодный запрос, как у сводки: 1С живьём, кеша на бэкенде нет.
    staleTime: 5 * 60_000,
  });
  const data = q.data;

  return (
    <div
      className="fixed inset-0 z-[70] flex items-center justify-center bg-slate-900/45 p-2 sm:p-4"
      onClick={onClose}
    >
      <div
        role="dialog"
        aria-label={title}
        className="flex h-[90dvh] w-[min(96vw,1800px)] flex-col overflow-hidden rounded-2xl bg-white"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start gap-2 border-b px-4 py-2.5">
          <div className="min-w-0 flex-1">
            <div className="break-words text-[15px] font-semibold leading-6">{title}</div>
            {data && !q.error && (
              <div className="text-xs text-muted-foreground tabular-nums">
                {ruNum.format(data.items.length)} поз. · {ruNum.format(data.total)} шт
              </div>
            )}
          </div>
          <Button variant="ghost" size="sm" aria-label="Закрыть" onClick={onClose}>
            <X className="size-4" />
          </Button>
        </div>

        {q.isLoading || q.error ? (
          <LoadState
            isLoading={q.isLoading}
            error={q.error}
            onRetry={() => void q.refetch()}
            className="flex flex-col items-center justify-center gap-3 p-8 text-center"
          />
        ) : (
          data && (
            // Без вертикального padding: иначе липкие шапка и «Итого» встают
            // с зазором, в который видно прокручиваемые строки.
            <div className="min-h-0 flex-1 overflow-auto">
              {data.warnings.length > 0 && (
                <div className="m-3 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-[13px] text-amber-900">
                  <div className="flex items-center gap-2 font-medium">
                    <AlertTriangle className="size-4 shrink-0" />
                    Картинка неполная
                  </div>
                  <ul className="mt-1 list-disc space-y-0.5 pl-5">
                    {data.warnings.map((w) => (
                      <li key={w} className="break-words">
                        {w}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {data.items.length === 0 ? (
                <p className="p-4 text-[13px] text-muted-foreground">Позиций нет</p>
              ) : (
                <table className="w-full min-w-[760px] table-fixed text-xs leading-5">
                  <colgroup>
                    <col className="w-[116px]" />
                    <col />
                    <col className="w-[88px]" />
                    <col className="w-[84px]" />
                    <col className="w-[84px]" />
                    <col className="w-[64px]" />
                    <col className="w-[92px]" />
                  </colgroup>
                  <thead>
                    <tr className="text-left text-[11px] text-muted-foreground [&>th]:sticky [&>th]:top-0 [&>th]:z-10 [&>th]:bg-slate-50 [&>th]:shadow-[inset_0_-1px_0_hsl(var(--border))] [&>th]:whitespace-nowrap [&>th]:py-1 [&>th]:font-medium">
                      <th className="pl-4 pr-2">Артикул</th>
                      <th className="px-2">Товар</th>
                      <th className="px-2">Партия</th>
                      <th className="px-2">Произведён</th>
                      <th className="px-2">Годен до</th>
                      <th className="px-2 text-right" title="Остаточный срок годности">
                        ОСГ
                      </th>
                      <th className="pl-2 pr-4 text-right">Кол-во, шт</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.items.map((i) => (
                      <tr
                        key={`${i.nomenclature_ref}|${i.characteristic_ref}|${i.batch_ref}`}
                        className="border-b border-slate-100 even:bg-slate-50/60 hover:bg-sky-50/60 [&>td]:py-1"
                      >
                        <td
                          className="whitespace-nowrap px-2 pl-4 tabular-nums"
                          title={i.article || undefined}
                        >
                          <span className="block truncate">{i.article || "—"}</span>
                        </td>
                        <td className="px-2">
                          {/* Одна строка: название обрезается, бейдж характеристики
                              стоит сразу за ним и виден всегда. */}
                          <div className="flex min-w-0 items-center gap-1.5">
                            <span className="min-w-0 truncate" title={i.name}>
                              {i.name}
                            </span>
                            {i.characteristic && (
                              <span
                                className="max-w-[40%] shrink-0 truncate whitespace-nowrap rounded border border-slate-200 bg-white px-1 text-[11px] leading-4 text-slate-600"
                                title={`Характеристика: ${i.characteristic}`}
                              >
                                {i.characteristic}
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="truncate px-2" title={i.batch ? `Партия ${i.batch}` : undefined}>
                          {i.batch ? (
                            <>
                              <span className="text-muted-foreground">№ </span>
                              {i.batch}
                            </>
                          ) : (
                            <span className="text-muted-foreground">—</span>
                          )}
                        </td>
                        <td className="whitespace-nowrap px-2 tabular-nums">
                          {fmtDate(i.produced) || <span className="text-muted-foreground">—</span>}
                        </td>
                        <td className="whitespace-nowrap px-2 tabular-nums">
                          {fmtDate(i.expires) || <span className="text-muted-foreground">—</span>}
                        </td>
                        <td className="whitespace-nowrap px-2 text-right">
                          {i.shelf_life_pct === null ? (
                            <span className="text-muted-foreground">—</span>
                          ) : (
                            <span className="rounded-full bg-slate-100 px-1.5 text-[11px] leading-4 tabular-nums text-slate-700">
                              {fmtPercent(i.shelf_life_pct)}
                            </span>
                          )}
                        </td>
                        <td className="whitespace-nowrap px-2 pr-4 text-right font-semibold tabular-nums">
                          {ruNum.format(i.qty)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="font-semibold [&>td]:sticky [&>td]:bottom-0 [&>td]:bg-white [&>td]:shadow-[inset_0_1px_0_hsl(var(--border))] [&>td]:py-1.5">
                      <td className="pl-4 pr-2" colSpan={6}>
                        Итого
                      </td>
                      <td className="pl-2 pr-4 text-right tabular-nums">{ruNum.format(data.total)}</td>
                    </tr>
                  </tfoot>
                </table>
              )}
            </div>
          )
        )}
      </div>
    </div>
  );
}
